import {
  formatBrowserCode,
  getDefaultPttBrowserCode,
  isNormalizedModifier,
} from "@bezoned/shared/features/push-to-talk/push-to-talk.utils.js";
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Text,
} from "@fluentui/react-components";
import { useEffect, useRef } from "react";

import { useSetPushToTalkButton } from "../../table-call/use-set-push-to-talk-button.js";

/**
 * Component for managing push-to-talk key settings
 */
export const SettingsUserPushToTalk = () => {
  const {
    currentKey,
    isRecording,
    tempKey,
    validationError,
    startRecording,
    cancelRecording,
    handleKeyDown,
    resetKey,
  } = useSetPushToTalkButton();

  const inputRef = useRef<HTMLInputElement>(null);
  const defaultPttCode = getDefaultPttBrowserCode();
  const defaultPttDisplay = formatBrowserCode(defaultPttCode);

  useEffect(() => {
    if (isRecording && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isRecording]);

  return (
    <div className="flex flex-col gap-4">
      <Text size={300} weight="semibold">
        Push-to-talk key
      </Text>

      <Input
        appearance={isRecording ? "filled-darker" : "outline"}
        className="focus:border-primary-500 w-full max-w-xs"
        disabled={!isRecording}
        id="key-input"
        input={{ ref: inputRef }}
        onKeyDown={handleKeyDown}
        placeholder="No key set"
        readOnly
        value={
          isRecording
            ? tempKey.display || "Press a key or mouse button..."
            : currentKey.display
        }
      />

      {Boolean(validationError) && (
        <MessageBar intent="error">
          <MessageBarBody>{validationError}</MessageBarBody>
        </MessageBar>
      )}

      {isNormalizedModifier(currentKey.code) && (
        <MessageBar intent="info">
          <MessageBarBody>
            Both left and right keys will activate push-to-talk.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className="flex items-center gap-2">
        <Button
          appearance="primary"
          onClick={isRecording ? cancelRecording : startRecording}
        >
          {isRecording ? "Cancel" : "Set Key"}
        </Button>

        <Button
          appearance="transparent"
          disabled={isRecording}
          onClick={resetKey}
        >
          Reset to default ({defaultPttDisplay})
        </Button>
      </div>
    </div>
  );
};
